import { motion } from "framer-motion";
import { ExternalLink } from "lucide-react";

const CaseStudyCard = ({ caseStudy, index }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="bg-[#1A1F26] border border-[#30363D] rounded-2xl overflow-hidden hover:border-[#007AFF]/50 transition-all group"
      data-testid={`case-study-card-${caseStudy.id}`}
    >
      {/* Image */}
      <div className="relative overflow-hidden h-48">
        <img
          src={caseStudy.image}
          alt={caseStudy.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1A1F26] via-[#1A1F26]/20 to-transparent"></div>
        
        {/* Industry Badge */}
        <div className="absolute top-4 left-4">
          <span className="px-3 py-1 rounded-full bg-black/60 backdrop-blur-sm text-xs text-white/80 border border-white/10">
            {caseStudy.industry}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        <a 
          href={caseStudy.link} 
          target="_blank" 
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 hover:text-[#007AFF] transition-colors mb-3"
        >
          <h3 className="text-xl font-bold">{caseStudy.title}</h3>
          <ExternalLink className="w-4 h-4 opacity-60" />
        </a>

        {/* Platforms */}
        <div className="flex flex-wrap gap-2 mb-4">
          {caseStudy.platforms?.slice(0, 3).map((platform) => (
            <span key={platform} className="px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-xs text-[#8B949E]">
              {platform}
            </span>
          ))}
          {caseStudy.platforms?.length > 3 && (
            <span className="px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-xs text-[#8B949E]">
              +{caseStudy.platforms.length - 3}
            </span>
          )}
        </div>

        <p className="text-[#8B949E] text-sm leading-relaxed mb-4">{caseStudy.description}</p>

        {/* Results */}
        <div className="flex flex-wrap gap-4 pt-4 border-t border-[#30363D]">
          {caseStudy.results?.map((result, idx) => (
            <div key={idx} className="text-center">
              <div className="text-xl font-bold text-[#00C853]">{result.value}</div>
              <div className="text-xs text-[#8B949E]">{result.label}</div>
            </div>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

const CaseStudies = ({ caseStudies }) => {
  return (
    <section id="case-studies" className="py-20 md:py-28" data-testid="case-studies-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-sm text-[#007AFF] font-medium uppercase tracking-wider">Case Studies</span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mt-4 mb-6">
            Proven Results <span className="gradient-text">Across Industries</span>
          </h2>
          <p className="text-[#8B949E] max-w-2xl mx-auto text-lg">
            Real success stories from brands that trusted us to drive their app growth and revenue.
          </p>
        </motion.div>

        {/* Case Studies Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {caseStudies.map((caseStudy, index) => (
            <CaseStudyCard key={caseStudy.id} caseStudy={caseStudy} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default CaseStudies;
